import React from 'react';
import { Page, Text, View, Document, StyleSheet, Image } from '@react-pdf/renderer';
import { 
    COMPANY_NAME, 
    COMPANY_LOGO_PATH, 
    REPORT_FOOTER_TEXT 
} from '../appsettings';

// Create styles
const styles = StyleSheet.create({
    page: {
        flexDirection: 'column',
        backgroundColor: '#ffffff',
        padding: 30,
        fontFamily: 'Helvetica',
    },
    headerSection: {
        marginBottom: 20,
        borderBottomWidth: 2,
        borderBottomColor: '#112233',
        paddingBottom: 10,
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center'
    },
    logo: {
        width: 80,
        height: 80
    },
    title: {
        fontSize: 24,
        fontWeight: 'bold',
        color: '#112233',
        textTransform: 'uppercase'
    },
    subTitle: {
        fontSize: 10,
        color: '#666'
    },
    sectionTitle: {
        fontSize: 14,
        fontWeight: 'bold',
        marginBottom: 10,
        marginTop: 15,
        backgroundColor: '#f0f0f0',
        padding: 5,
        color: '#333'
    },
    row: {
        flexDirection: 'row',
        marginBottom: 5,
    },
    column: {
        flexDirection: 'column',
        flexGrow: 1,
        width: '50%'
    },
    label: {
        width: 100,
        fontSize: 10,
        fontWeight: 'bold',
        color: '#555',
    },
    value: {
        fontSize: 10,
        color: '#000',
        flex: 1
    },
    descriptionBox: {
        marginTop: 10,
        padding: 10,
        borderWidth: 1,
        borderColor: '#eee',
        backgroundColor: '#fafafa',
        fontSize: 10
    },
    // Table Styles
    table: {
        display: "table",
        width: "auto",
        borderStyle: "solid",
        borderWidth: 1,
        borderRightWidth: 0,
        borderBottomWidth: 0,
        borderColor: '#bfbfbf',
        marginTop: 10
    },
    tableRow: {
        margin: "auto",
        flexDirection: "row"
    },
    tableColHeader: {
        width: "20%",
        borderStyle: "solid",
        borderWidth: 1,
        borderLeftWidth: 0,
        borderTopWidth: 0,
        borderColor: '#bfbfbf',
        backgroundColor: '#f3f3f3',
        padding: 5
    },
    tableCol: {
        width: "20%",
        borderStyle: "solid",
        borderWidth: 1,
        borderLeftWidth: 0,
        borderTopWidth: 0,
        borderColor: '#bfbfbf',
        padding: 5
    },
    tableCellHeader: {
        margin: "auto",
        fontSize: 10,
        fontWeight: 'bold'
    },
    tableCell: {
        margin: "auto",
        fontSize: 10
    },
    footer: {
        position: 'absolute',
        bottom: 30,
        left: 30,
        right: 30,
        fontSize: 8,
        textAlign: 'center',
        color: 'grey',
        borderTopWidth: 1,
        borderColor: '#eee',
        paddingTop: 10
    }
});

const InventoryItemReportPDF = ({ item, records }) => {
    if (!item) return <Document><Page><Text>No Data</Text></Page></Document>;

    const stripHtml = (html) => {
        if (!html) return "N/A";
        return html.replace(/<[^>]*>?/gm, '');
    };

    const formatDate = (dateString) => {
        if (!dateString) return "-";
        return new Date(dateString).toLocaleDateString();
    };

    return (
        <Document title={`Item_${item.item_code}_${new Date().toLocaleString()}`} author={COMPANY_NAME}>
            <Page size="A4" style={styles.page}>
                
                {/* Header */}
                <View style={styles.headerSection}>
                    <View>
                        <Text style={styles.title}>Inventory Item</Text>
                        <Text style={styles.subTitle}>Generated on {new Date().toLocaleString()}</Text>
                    </View>
                    <View>
                        <Image source={{ uri: COMPANY_LOGO_PATH }} style={styles.logo} />
                    </View>
                </View>

                {/* Item Details */}
                <Text style={styles.sectionTitle}>Item Details</Text>
                <View style={{ flexDirection: 'row' }}>
                    <View style={styles.column}>
                        <View style={styles.row}>
                            <Text style={styles.label}>Item Code:</Text>
                            <Text style={styles.value}>{item.item_code}</Text>
                        </View>
                        <View style={styles.row}>
                            <Text style={styles.label}>Name:</Text>
                            <Text style={styles.value}>{item.name}</Text>
                        </View>
                        <View style={styles.row}>
                            <Text style={styles.label}>Type:</Text>
                            <Text style={styles.value}>{item.type}</Text>
                        </View>
                        <View style={styles.row}>
                            <Text style={styles.label}>Category:</Text>
                            <Text style={styles.value}>{item.category}</Text>
                        </View>
                    </View>
                    <View style={styles.column}>
                        <View style={styles.row}>
                            <Text style={styles.label}>Register Date:</Text>
                            <Text style={styles.value}>{item.register_date || '-'}</Text>
                        </View>
                        <View style={styles.row}>
                            <Text style={styles.label}>EOL Date:</Text>
                            <Text style={styles.value}>{item.eol_date || '-'}</Text>
                        </View>
                        <View style={styles.row}>
                            <Text style={styles.label}>Status:</Text>
                            <Text style={{...styles.value, color: Number(item.status) === 1 ? 'green' : 'red'}}>
                                {Number(item.status) === 1 ? 'Active' : 'Inactive'}
                            </Text>
                        </View>
                    </View>
                </View>

                {/* Description */}
                <Text style={styles.sectionTitle}>Description</Text>
                <View style={styles.descriptionBox}>
                    <Text>{stripHtml(item.description)}</Text>
                </View>

                {/* Note */}
                <Text style={styles.sectionTitle}>Note</Text>
                <View style={styles.row}>
                    <Text style={styles.value}>{item.note || 'No notes available.'}</Text>
                </View>

                {/* Service Records Table */}
                <Text style={styles.sectionTitle}>Service Records</Text>
                <View style={styles.table}>
                    {/* Table Header */}
                    <View style={styles.tableRow}>
                        <View style={styles.tableColHeader}><Text style={styles.tableCellHeader}>Date</Text></View>
                        <View style={styles.tableColHeader}><Text style={styles.tableCellHeader}>Type</Text></View>
                        <View style={styles.tableColHeader}><Text style={styles.tableCellHeader}>Status</Text></View>
                        <View style={{...styles.tableColHeader, width: '40%'}}><Text style={styles.tableCellHeader}>Note</Text></View>
                    </View>

                    {/* Table Body */}
                    {records && records.length > 0 ? (
                        records.map((record, index) => (
                            <View style={styles.tableRow} key={index}>
                                <View style={styles.tableCol}>
                                    <Text style={styles.tableCell}>{formatDate(record.date)}</Text>
                                </View>
                                <View style={styles.tableCol}>
                                    <Text style={styles.tableCell}>{record.type}</Text>
                                </View>
                                <View style={styles.tableCol}>
                                    <Text style={styles.tableCell}>{record.record_status}</Text>
                                </View>
                                <View style={{...styles.tableCol, width: '40%'}}>
                                    <Text style={styles.tableCell}>{record.note || '-'}</Text>
                                </View>
                            </View>
                        ))
                    ) : (
                        <View style={styles.tableRow}>
                            <View style={{...styles.tableCol, width: '100%'}}>
                                <Text style={styles.tableCell}>No service records found.</Text>
                            </View>
                        </View>
                    )}
                </View>

                <Text style={styles.footer}>
                    Confidential Report • {item.item_code} • {REPORT_FOOTER_TEXT}
                </Text>
            </Page>
        </Document>
    );
};

export default InventoryItemReportPDF;
